import React, { useCallback, useContext, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import logging from 'src/services/logging';
import _ from 'lodash';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Matchup } from 'src/models';
import ResultsAccordion from 'src/components/Results/ResultsAccordion';
import { CycleContext } from 'src/context/CycleContext';

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

export const ResultsPage = () => {
  const cycle = useContext(CycleContext);
  const [value, setValue] = useState(cycle.activeRound - 1);
  const [fetchingMatchups, setFetchingMatchups] = useState<boolean>(false);
  const [firstRoundMatchups, setFirstRoundMatchups] = useState<Matchup[]>([]);
  const [secondRoundMatchups, setSecondRoundMatchups] = useState<Matchup[]>([]);
  const [thirdRoundMatchups, setThirdRoundMatchups] = useState<Matchup[]>([]);
  const [fourthRoundMatchups, setFourthRoundMatchups] = useState<Matchup[]>([]);
  const resultsShownMessage = 'Results will be shown once round has started';

  const fetchMatchups = useCallback(async () => {
    try {
      logging.info('Fetching matchups...', 'ResultsPage');
      setFetchingMatchups(true);
      const q = query(
        collection(db, 'matchups'),
        where('season', '==', cycle.season)
      );
      const snapshot = await getDocs(q);
      const matchups: Matchup[] = [];
      snapshot.forEach((match) => {
        matchups.push({
          gamePk: match.data().gamePk,
          playoffRound: match.data().playoffRound,
          playoffSeriesLetter: match.data().playoffSeriesLetter,
          season: match.data().season,
          startTime: match.data().startTime,
          teams: match.data().teams,
        });
      });
      const sortedMatchups = _.orderBy(
        matchups,
        ['playoffSeriesLetter'],
        ['asc']
      );
      setFirstRoundMatchups(
        sortedMatchups.filter((matches) => matches.playoffRound === 1)
      );
      setSecondRoundMatchups(
        sortedMatchups.filter((matches) => matches.playoffRound === 2)
      );
      setThirdRoundMatchups(
        sortedMatchups.filter((matches) => matches.playoffRound === 3)
      );
      setFourthRoundMatchups(
        sortedMatchups.filter((matches) => matches.playoffRound === 4)
      );
    } catch (error) {
      logging.error(error, 'ResultsPage');
    } finally {
      setFetchingMatchups(false);
    }
  }, [cycle.season]);

  useEffect(() => {
    fetchMatchups();
  }, [fetchMatchups]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (fetchingMatchups) {
    return (
      <Backdrop open={fetchingMatchups}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Container maxWidth='md' sx={{ mt: 3 }}>
      <Card>
        <CardHeader
          title={`RESULTS ${cycle.season.toString().slice(4, 8)}`}
          titleTypographyProps={{ flex: '1 1 100%', variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Tabs
            variant='fullWidth'
            value={value}
            onChange={handleChange}
            aria-label='tabs-results-page'
          >
            <Tab
              label='RD 1'
              {...a11yProps(0)}
              disabled={!firstRoundMatchups.length}
            />
            <Tab
              label='RD 2'
              {...a11yProps(1)}
              disabled={!secondRoundMatchups.length}
            />
            <Tab
              label='RD 3'
              {...a11yProps(2)}
              disabled={!thirdRoundMatchups.length}
            />
            <Tab
              label='RD 4'
              {...a11yProps(3)}
              disabled={!fourthRoundMatchups.length}
            />
          </Tabs>
          <TabPanel value={value} index={0}>
            {firstRoundMatchups.length > 0 ? (
              <ResultsAccordion matchups={firstRoundMatchups} />
            ) : (
              <Typography style={{ paddingTop: 24 }}>
                {resultsShownMessage}
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            {secondRoundMatchups.length > 0 ? (
              <ResultsAccordion matchups={secondRoundMatchups} />
            ) : (
              <Typography style={{ paddingTop: 24 }}>
                {resultsShownMessage}
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={value} index={2}>
            {thirdRoundMatchups.length > 0 ? (
              <ResultsAccordion matchups={thirdRoundMatchups} />
            ) : (
              <Typography style={{ paddingTop: 24 }}>
                {resultsShownMessage}
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={value} index={3}>
            {fourthRoundMatchups.length > 0 ? (
              <ResultsAccordion matchups={fourthRoundMatchups} />
            ) : (
              <Typography style={{ paddingTop: 24 }}>
                {resultsShownMessage}
              </Typography>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Container>
  );
};
